// components/CustomTable.tsx
import React from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  Table,
} from '@tanstack/react-table'

interface TableProps<T> {
  data: T[]
  columns: ColumnDef<T, any>[]
  pageSize?: number
  hidePagination?: boolean
}

function CustomTable<T extends object>({ data, columns, pageSize = 10, hidePagination = false }: TableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize,
      },
    },
  })

  return (
    <div className="table-responsive">
      <table className="table table-bordered border-secondary align-middle w-100">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr className="bg-secondary align-middle" key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th className='text-gray-700' key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      {!hidePagination && (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div>
          <button
            className="btn btn-sm btn-light me-2"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </button>
        </div>
      </div>
      )}
    </div>
  )
}

export default CustomTable
